import { ServiceIconShell } from '../ServiceIconShell';

import { Img } from '../../display/Img';

import {
  type ServiceIconSize,
  ServiceIconConfig,
  type LegacyServiceIconSize,
} from './constants';

type ServiceIconProps = {
  /**
   * Applies a custom alt attribute to the underlying Img component.
   */
  alt?: string;
  /**
   * Inform screen-readers that they should ignore the image. Image will still be visually displayed.
   */
  'aria-hidden'?: boolean;
  /**
   * Applies a shadow to the ServiceIcon. It is recommended to use `hasShadow` at sizes 80px and larger.
   */
  hasShadow?: boolean;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be contained in a larger, bordered container.
   */
  hasBorder?: boolean;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
  /**
   * The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   */
  serviceName: string;
  /**
   * A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  src: string;
  /**
   * Determines the size of the shell and icon. ** `xx-small` is only meant to be used as a child of `Tag`.**
   *
   * `size` maps to the following shell sizes:
   * - xx-small: 16x16
   * - x-small: 20x20
   * - small (20): 24x24
   * - medium (40): 36x36
   * - large (60): 48x48
   */
  size: ServiceIconSize | LegacyServiceIconSize;
};

/**
 * Renders an icon representing a service (also known as an app or integration).
 */
export const ServiceIcon = ({
  alt,
  'aria-hidden': ariaHidden = false,
  hasBorder = true,
  hasShadow = false,
  hasWhiteBackground = false,
  serviceName,
  size: sizeFromProps,
  src,
}: ServiceIconProps) => {
  const size = sizeFromProps in ServiceIconConfig ? sizeFromProps : 'medium';
  const iconSize = ServiceIconConfig[size].iconSize;

  return (
    <ServiceIconShell
      hasBorder={hasBorder}
      hasShadow={hasShadow}
      hasWhiteBackground={hasWhiteBackground}
      size={size}
    >
      <Img
        alt={alt || `${serviceName} logo`}
        aria-hidden={ariaHidden}
        borderRadius="none"
        height={iconSize}
        width={iconSize}
        objectFit="contain"
        src={src}
      />
    </ServiceIconShell>
  );
};
